import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Link, Unlink, Shield, ExternalLink, ArrowUpRight, ArrowDownRight, X } from "lucide-react";

const fmtNum = (n, dp = 2) =>
  n === null || n === undefined || isNaN(n)
    ? "—"
    : Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

export default function AlpacaPanel() {
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderForm, setOrderForm] = useState({
    symbol: "AAPL", side: "buy", type: "market", qty: "1", limit_price: "", stop_price: "",
  });
  const [placing, setPlacing] = useState(false);

  const loadStatus = () =>
    api.get("/broker/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false }));

  const loadAll = async () => {
    try {
      const [a, p, o] = await Promise.all([
        api.get("/broker/account"),
        api.get("/broker/positions"),
        api.get("/broker/orders", { params: { status: "open" } }),
      ]);
      setAccount(a.data);
      setPositions(p.data);
      setOrders(o.data);
    } catch {
      loadStatus();
    }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => {
    if (status?.connected) loadAll();
  }, [status?.connected]);

  const connect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      await api.post("/broker/connect", { key_id: keyId.trim(), secret: secret.trim() });
      toast.success("Connected to Alpaca Paper");
      setKeyId("");
      setSecret("");
      loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Connection failed");
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    await api.post("/broker/disconnect").catch(() => {});
    toast.success("Disconnected");
    setStatus({ connected: false });
    setAccount(null); setPositions([]); setOrders([]);
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const payload = {
        symbol: orderForm.symbol.trim().toUpperCase(),
        side: orderForm.side, type: orderForm.type,
        time_in_force: "day", qty: Number(orderForm.qty),
      };
      if (orderForm.type === "limit") payload.limit_price = Number(orderForm.limit_price);
      if (orderForm.type === "stop") payload.stop_price = Number(orderForm.stop_price);
      const { data } = await api.post("/broker/orders", payload);
      toast.success(`Order ${data.id?.slice(0, 8) || ""} submitted at Alpaca`);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Order failed");
    } finally {
      setPlacing(false);
    }
  };

  const cancelOrder = async (id) => {
    await api.delete(`/broker/orders/${id}`).catch(() => {});
    toast.success("Order cancelled");
    loadAll();
  };

  if (status === null) return <p className="text-sm text-gray-500">Loading Alpaca…</p>;

  if (!status.connected) {
    return (
      <Card className="border border-gray-200 rounded-2xl p-6 max-w-2xl" data-testid="broker-connect-card">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={16} className="text-gain" />
          <p className="text-sm font-medium">Your keys are Fernet-encrypted at rest.</p>
        </div>
        <p className="text-[11px] text-gray-500 mb-4 leading-relaxed">
          Never paste keys into a browser you don&apos;t own. Alpaca only shows the secret once when you generate it.
        </p>
        <ol className="text-sm text-gray-600 space-y-1 mb-6 list-decimal ml-5">
          <li>
            Sign up (free) at{" "}
            <a
              href="https://alpaca.markets/"
              target="_blank"
              rel="noreferrer"
              className="text-black underline inline-flex items-center gap-1"
            >
              alpaca.markets <ExternalLink size={10} />
            </a>{" "}
            → Paper Trading.
          </li>
          <li>Generate an <span className="font-mono">API Key + Secret</span> in the dashboard.</li>
          <li>Paste both below. Alpaca only shows the secret once — copy it immediately.</li>
        </ol>
        <form onSubmit={connect} className="space-y-4" data-testid="broker-connect-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">API Key ID</Label>
            <Input
              data-testid="broker-key-id" required value={keyId}
              onChange={(e) => setKeyId(e.target.value)} placeholder="PKXXXXXXXXXX"
              className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">API Secret</Label>
            <Input
              data-testid="broker-secret" required type="password" value={secret}
              onChange={(e) => setSecret(e.target.value)} placeholder="Paste the secret shown once"
              className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
            />
          </div>
          <Button
            data-testid="broker-connect-btn" type="submit" disabled={connecting}
            className="rounded-full bg-brand text-white hover:bg-brand-deep h-11 px-6"
          >
            <Link size={14} className="mr-2" /> {connecting ? "Validating…" : "Connect Alpaca Paper"}
          </Button>
        </form>
      </Card>
    );
  }

  return (
    <>
      <Card className="border border-gray-200 rounded-2xl p-6" data-testid="broker-account-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Account</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge className="bg-gain text-white text-[10px]" data-testid="broker-connected-badge">Connected</Badge>
              <p className="text-sm font-mono text-gray-500">
                {status.key_id_masked} · last validated{" "}
                {status.last_validated_at ? new Date(status.last_validated_at).toLocaleString() : "just now"}
              </p>
            </div>
          </div>
          <Button data-testid="broker-disconnect-btn" variant="outline" onClick={disconnect} className="rounded-full border-gray-200">
            <Unlink size={12} className="mr-2" /> Disconnect
          </Button>
        </div>
        {account && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <Stat label="Equity" value={`$${fmtNum(account.equity)}`} testId="broker-equity" />
            <Stat label="Buying power" value={`$${fmtNum(account.buying_power)}`} testId="broker-buying-power" />
            <Stat label="Cash" value={`$${fmtNum(account.cash)}`} testId="broker-cash" />
            <Stat
              label="Daily P&L"
              value={`$${fmtNum(Number(account.equity) - Number(account.last_equity || account.equity))}`}
              testId="broker-daily-pnl"
            />
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="broker-positions-card">
          <p className="font-display font-semibold text-lg mb-4">Positions</p>
          {positions.length === 0 ? (
            <p className="text-sm text-gray-500">No open positions.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {positions.map((p) => {
                const pnl = Number(p.unrealized_pl);
                return (
                  <div key={p.symbol} className="flex items-center justify-between py-3" data-testid={`broker-position-${p.symbol}`}>
                    <div>
                      <p className="font-medium">{p.symbol}</p>
                      <p className="text-xs text-gray-500">{fmtNum(p.qty, 4)} @ ${fmtNum(p.avg_entry_price)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm">${fmtNum(p.market_value)}</p>
                      <p className={`text-xs font-mono ${pnl >= 0 ? "text-gain" : "text-loss"}`}>
                        {pnl >= 0 ? "+" : ""}${fmtNum(pnl)} ({fmtNum(Number(p.unrealized_plpc) * 100)}%)
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="broker-orders-card">
          <p className="font-display font-semibold text-lg mb-4">Open Alpaca orders</p>
          {orders.length === 0 ? (
            <p className="text-sm text-gray-500">No open orders at Alpaca.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between py-3" data-testid={`broker-order-${o.id}`}>
                  <div>
                    <p className="text-sm font-medium">
                      {o.side === "buy" ? (
                        <ArrowUpRight size={12} className="inline text-gain" />
                      ) : (
                        <ArrowDownRight size={12} className="inline text-loss" />
                      )}{" "}
                      <span className={o.side === "buy" ? "text-gain" : "text-loss"}>{o.side.toUpperCase()}</span>{" "}
                      {fmtNum(o.qty, 4)} {o.symbol}
                    </p>
                    <p className="text-xs text-gray-500">
                      {o.type} · TIF {o.time_in_force}
                      {o.limit_price && ` · limit $${o.limit_price}`}
                      {o.stop_price && ` · stop $${o.stop_price}`}
                    </p>
                  </div>
                  <button
                    onClick={() => cancelOrder(o.id)}
                    data-testid={`broker-cancel-${o.id}`}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-loss"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="border border-gray-200 rounded-2xl p-6 max-w-2xl mt-6" data-testid="broker-order-form-card">
        <p className="font-display font-semibold text-lg mb-4">Place a stock order at Alpaca</p>
        <form onSubmit={placeOrder} className="grid grid-cols-2 gap-4" data-testid="broker-order-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Symbol</Label>
            <Input
              data-testid="broker-order-symbol" required value={orderForm.symbol}
              onChange={(e) => setOrderForm({ ...orderForm, symbol: e.target.value })}
              className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono uppercase"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Quantity</Label>
            <Input
              data-testid="broker-order-qty" type="number" step="any" min="0" required value={orderForm.qty}
              onChange={(e) => setOrderForm({ ...orderForm, qty: e.target.value })}
              className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Side</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {["buy", "sell"].map((s) => (
                <button
                  key={s} type="button" data-testid={`broker-side-${s}`}
                  onClick={() => setOrderForm({ ...orderForm, side: s })}
                  className={`rounded-full py-2 text-xs font-medium ${
                    orderForm.side === s
                      ? s === "buy" ? "bg-gain text-white" : "bg-loss text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Type</Label>
            <div className="grid grid-cols-3 gap-1 mt-2 rounded-full bg-gray-100 p-1">
              {["market", "limit", "stop"].map((t) => (
                <button
                  key={t} type="button" data-testid={`broker-type-${t}`}
                  onClick={() => setOrderForm({ ...orderForm, type: t })}
                  className={`rounded-full py-1.5 text-xs font-medium ${
                    orderForm.type === t ? "bg-white text-black shadow-sm" : "text-gray-600"
                  }`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
          {orderForm.type === "limit" && (
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Limit price</Label>
              <Input
                data-testid="broker-order-limit" type="number" step="any" required
                value={orderForm.limit_price}
                onChange={(e) => setOrderForm({ ...orderForm, limit_price: e.target.value })}
                className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
              />
            </div>
          )}
          {orderForm.type === "stop" && (
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Stop price</Label>
              <Input
                data-testid="broker-order-stop" type="number" step="any" required
                value={orderForm.stop_price}
                onChange={(e) => setOrderForm({ ...orderForm, stop_price: e.target.value })}
                className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
              />
            </div>
          )}
          <div className="col-span-2">
            <Button
              data-testid="broker-place-order" type="submit" disabled={placing}
              className={`w-full h-11 rounded-full font-medium text-white ${
                orderForm.side === "buy" ? "bg-gain hover:opacity-90" : "bg-loss hover:opacity-90"
              }`}
            >
              {placing ? "Placing…" : `${orderForm.side === "buy" ? "Buy" : "Sell"} ${orderForm.symbol.toUpperCase()} on Alpaca`}
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</p>
      <p className="font-display text-xl font-medium tabular-nums mt-1">{value}</p>
    </div>
  );
}
